import { AnyFeatureHook } from '@common/hooks';
import { NavigationTabsOption } from '@common/types/link';
import { BaseFeatureArgs } from '@common/types/queryState';
import { CLIP_FACET_CONFIG } from '@features/advancedSearch/constants';
import { Clip } from '@features/Clip';
import { useClipColumns } from '@features/Clip/hooks/useClipColumns';
import { useFacetedFeature } from '@features/facetingEngine/hooks/useFacetedFeature';
import { TableCard, TableCardProps } from '@features/tableEngine/components';
import { FeatureConfiguration } from '@features/tableEngine/types';
import { Flex } from '@radix-ui/themes';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useClipActions } from '../hooks/useClipActions';
import { useClipQuickFilters } from '../hooks/useClipQuickFilters';

type ManagedTableProps =
    | 'data'
    | 'itemCount'
    | 'pageCount'
    | 'searchState'
    | 'paginationState'
    | 'sortingState'
    | 'facet'
    | 'filterState'
    | 'config'
    | 'tabOptions'
    | 'renderFacets';

export interface SharedClipTableProps<TArgs extends BaseFeatureArgs>
    extends Omit<TableCardProps<Clip>, ManagedTableProps> {
    queryHook: AnyFeatureHook<Clip, TArgs>;
    prefix?: string;

    initialPageSize?: number;
    initialSorting?: { id: string; desc: boolean }[];
    featureArgs?: Partial<TArgs>;

    tabOptions?: NavigationTabsOption<string>[];
    configOverrides?: Partial<FeatureConfiguration<Clip>>;
}

export const SharedClipTable = <TArgs extends BaseFeatureArgs>({
    queryHook,
    prefix,
    initialPageSize = 25,
    initialSorting = [{ id: 'title', desc: false }],
    featureArgs,
    tabOptions,
    configOverrides = {},
    ...tableProps
}: SharedClipTableProps<TArgs>) => {
    const { t } = useTranslation(['clipTable', 'clipDetails']);

    const { baseActions, clipActionModals } = useClipActions();
    const columns = useClipColumns();
    const queryOptions = useMemo(() => ({ prefix }), [prefix]);

    const { filterGenerator, quickFiltersController } = useClipQuickFilters(
        queryHook,
        featureArgs,
        queryOptions,
    );

    const pageSizes = [...Array(5)].map((_, i) => (i + 1) * initialPageSize);

    const resolvedFeatureArgs = {
        ...featureArgs,
        fixedFilterGenerators: [
            filterGenerator,
            ...(featureArgs?.fixedFilterGenerators || []),
        ],
    } as TArgs;

    const { result, facetNode } = useFacetedFeature({
        queryHook,
        initialArgs: {
            pageSize: initialPageSize,
            sorting: initialSorting,
        },
        featureArgs: resolvedFeatureArgs,
        baseConfig: CLIP_FACET_CONFIG,
        labels: {
            title: t('filters.title', { ns: 'clipTable' }),
            getSectionLabel: (key) =>
                t(`metadata.${key}`, { ns: 'clipDetails' }),
            getValueLabel: (key, val) =>
                t(`filters.values.${key}.${val}`, { defaultValue: val }),
        },
        queryOptions,
    });

    const baseConfig: FeatureConfiguration<Clip> = {
        columns,
        search: {
            enabled: true,
            placeholder: t('search.placeholder', { ns: 'clipTable' }),
        },
        selection: { enabled: true, displayKey: 'clipName' },
        sorting: { enabled: true },
        pagination: { enabled: true, pageSizes },
        facets: { enabled: true },
        modes: { enabled: false },
        columnControl: { enabled: true },
        columnResizing: { enabled: false },
    };

    const finalConfig: FeatureConfiguration<Clip> = {
        ...baseConfig,
        ...configOverrides,
    };

    const finalActions = useMemo(
        () => [...baseActions, ...(tableProps.actions || [])],
        [baseActions, tableProps.actions],
    );

    return (
        <>
            <TableCard<Clip>
                {...result}
                {...tableProps}
                actions={finalActions}
                tabOptions={tabOptions}
                config={finalConfig}
                renderFacets={() => (
                    <Flex
                        direction='column'
                        style={{ height: '100%', minHeight: 0 }}
                    >
                        {quickFiltersController}
                        {facetNode}
                    </Flex>
                )}
            />

            {clipActionModals}
        </>
    );
};
